import { TransportOption } from './../model/transport-option';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { TranslationService } from '@sdbn/common';
import { DialogService, ResponseMessage, WidgetStyle } from '@sdbn/appclient';
import { ResponseStatus } from '@sdbn/appclient';
import { ActionType } from '../model/action-type.enum';
import { RoutingRule } from '../model/routing-rule';
import { RoutingRulePosition } from '../model/routing-rule-position';
import { StationLevel } from '../model/station-level.enum';
import { StationType } from '../model/station-type.enum';
import { RoutingRuleService } from '../service/routing-rule.service';
import { ServiceResponse } from '@sdbn/appclient';
import { ProcessingStatusModel } from 'src/tmc-yard/client/model/processing-status-model';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';
import { Subscription } from 'rxjs';
import { WidgetComponent } from '@sdbn/appclient';
import { ContextChange } from '@sdbn/appclient';
import { MasterDetailListEntry } from '@sdbn/common';
import { AbstractWidgetComponent } from '@sdbn/appclient';
import { TaskAssignmentService } from 'src/tmc-yard/components/task-assignment/service/task-assignment.service';
import { Task } from 'src/tmc-yard/components/task-assignment/model/task';
import { StructureSelection } from 'src/tmc-yard/common/structure-selection/structure-selection.model';
import { StructureSelectionService } from 'src/tmc-yard/common/structure-selection/structure-selection-dialog/structure-selection-dialog-service';
import { StationAssignmentStrategy } from 'src/tmc-yard/common/station-assignment-strategy/station-assignment-strategy.model';


export interface ActionTypeOption {
  option: ActionType;
  name: string;
}

@Component({
  selector: 'app-routing-rules',
  templateUrl: './routing-rule.component.html',
  styleUrls: ['./routing-rule.component.scss']
})
export class RoutingRuleComponent extends AbstractWidgetComponent implements OnInit, OnDestroy, WidgetComponent {

  yardConfigSubscription: Subscription;
  processingStatusModels: ProcessingStatusModel[] = [];

  rules: RoutingRule[];
  transportOptions: TransportOption[] = [];

  actionTypeOptions: ActionTypeOption[] = [];
  stationAssignmentStrategyOptions = new Array<StationAssignmentStrategy>();
  isLoading = false;

  selectedRule: RoutingRule;
  workingCopyRule: RoutingRule;

  responseMessages: ResponseMessage[];

  @ViewChild('routingRulesForm', { static: true }) form: NgForm;


  constructor(
    public translationService: TranslationService,
    private tmcContextService: TmcContextService,
    private structureSelectionService: StructureSelectionService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private rrs: RoutingRuleService,
    private taskAssignmentService: TaskAssignmentService) {
    super();
    this.yardConfigSubscription = this.tmcContextService.updated.subscribe(
      next => {
        if (next != null) {
          this.processingStatusModels = this.tmcContextService.getYardConfiguration().processingStatusModels;
        }
      }
    );
  }

  ngOnInit() {

    this.loadTransportOptions();

    for (const k of Object.keys(ActionType)) {
      this.actionTypeOptions.push({ option: ActionType[k], name: k });
    }
    for (const k of Object.keys(StationAssignmentStrategy)) {
      this.stationAssignmentStrategyOptions.push(StationAssignmentStrategy[k]);
    }
  }

  ngOnDestroy() {
    if (this.yardConfigSubscription) {
      this.yardConfigSubscription.unsubscribe();
    }
  }

  loadTransportOptions() {
    this.isLoading = true;
    this.rrs.getTransportOptions().subscribe(options => {

      this.transportOptions = options;

      const defaultOption = new TransportOption();
      defaultOption.id = -1;
      defaultOption.name = 'tmc.admin.routing-rule.rule.transport-option.default';
      this.transportOptions.push(defaultOption);

      this.loadRules();
      this.isLoading = false;
    }, error => {
      console.error(error);
      this.isLoading = false;
    });
  }

  loadRules() {
    this.isLoading = true;
    this.rrs.getRoutingRules().subscribe(rules => {

      let newRules = new Array<RoutingRule>();
      for (let rule of rules) {
        newRules.push(new RoutingRule(rule));
      }
      this.rules = newRules;


      if (rules.length > 0) {
        if (!this.selectedRule) {
          this.setSelectedRuleWithoutCheck(this.rules[0]);
        } else {
          // find selected rule and reset it
          this.selectedRule = this.rules.find(r => r.id === this.selectedRule.id);
        }
      } else {
        this.selectedRule = this.createNewRule();
        this.workingCopyRule = JSON.parse(JSON.stringify(this.selectedRule));
      }
      this.isLoading = false;
    }, error => {
      console.error(error);
      this.isLoading = false;
    });
  }

  setSelectedRuleWithoutCheck(rule: RoutingRule) {
    this.selectedRule = rule;
    this.workingCopyRule = JSON.parse(JSON.stringify(rule));
  }

  setSelectedRule(rule: RoutingRule) {

    if (this.selectedRule && this.selectedRule.id === rule.id) {
      return;
    }

    if (this.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.routing-rule.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.routing-rule.discard.confirm-dialog.message',
        confirm: () => {
          this.selectedRule = rule;
          this.workingCopyRule = JSON.parse(JSON.stringify(rule));
          this.responseMessages = [];
          this.form.form.markAsPristine();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.selectedRule = rule;
      this.workingCopyRule = JSON.parse(JSON.stringify(rule));
      this.responseMessages = [];
      this.form.form.markAsPristine();
    }
  }

  isSelected(rule: RoutingRule): boolean {
    return this.selectedRule && this.selectedRule.id === rule.id;
  }

  save() {
    // check for duplicate name
    if (this.duplicateNameExists()) {
      this.openDuplicateNameDialog();
    } else {

      if (this.workingCopyRule.id > 0) {
        this.saveRoutingRule(this.workingCopyRule);
      } else {
        this.createRoutingRule(this.workingCopyRule);
      }
    }
  }

  openDuplicateNameDialog() {
    this.dialogService.showInfoDialog(
        'tmc.admin.routing-rule.save.duplicate-name-dialog.title',
        'tmc.admin.routing-rule.save.duplicate-name-dialog.message');
  }

  cancel() {
    if (this.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.routing-rule.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.routing-rule.discard.confirm-dialog.message',
        confirm: () => {
          this.form.form.markAsPristine();
          if (this.workingCopyRule.id > 0) {
            this.setSelectedRuleWithoutCheck(this.selectedRule);
          } else {
            this.workingCopyRule = this.createNewRule();
          }
          this.responseMessages = [];
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.form.form.markAsPristine();
      if (this.workingCopyRule.id > 0) {
        this.setSelectedRuleWithoutCheck(this.selectedRule);
      } else {
        this.workingCopyRule = this.createNewRule();
      }
      this.responseMessages = [];
    }

  }

  createRoutingRule(rule: RoutingRule) {
    this.isLoading = true;
    this.rrs.createRoutingRule(rule).subscribe(response => {
      this.isLoading = false;
      if (response.status === ResponseStatus.SUCCESS) {
        this.selectedRule = null;
        this.workingCopyRule = this.createNewRule();
        this.loadRules();
        this.form.form.markAsPristine();
      } else if (this.hasDuplicateNameError(response)) {
        this.openDuplicateNameDialog();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.isLoading = false;
    });
  }

  saveRoutingRule(rule: RoutingRule) {
    // remove empty positions
    rule.positions = rule.positions.filter(pos => pos.stationType);

    this.isLoading = true;
    this.rrs.saveRoutingRule(rule).subscribe(response => {
      this.isLoading = false;
      if (response.status === ResponseStatus.SUCCESS) {
        this.loadRules();
        this.form.form.markAsPristine();
      } else if (this.hasDuplicateNameError(response)) {
        this.openDuplicateNameDialog();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.isLoading = false;
    });
  }

  hasDuplicateNameError(response: ServiceResponse) {
    return response.status === ResponseStatus.FAILED
      && response.responseMessages
      && response.responseMessages.length > 0
      && response.responseMessages[0].defaultMessage
      && response.responseMessages[0].defaultMessage === 'duplicate name';
  }

  deleteRoutingRule(rule: RoutingRule) {
    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc.admin.routing-rule.delete.confirm-dialog.title',
      messageKey: 'tmc.admin.routing-rule.delete.confirm-dialog.message',
      confirm: () => {
        this.isLoading = true;
        this.rrs.deleteRoutingRule(rule).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clear();
            this.loadRules();
          } else {
            this.responseMessages = response.responseMessages;
          }
        }, error => {
          console.log(error);
          this.isLoading = false;
        });
      }
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  isActionTypeDisabled(position: RoutingRulePosition) {
    return position.stationType === StationType.WAITING_AREA;
  }

  isWaitingForRetrieveDisabled(position: RoutingRulePosition) {
    return position.stationType === StationType.WAITING_AREA;
  }

  isCargoMandatoryDisabled(position: RoutingRulePosition) {
    return position.stationType === StationType.WAITING_AREA;
  }

  isPredictedDurationDisabled(position: RoutingRulePosition) {
    return position.stationType === StationType.WAITING_AREA;
  }

  isStationAssignmentStrategyRequired(position: RoutingRulePosition): boolean {
    return position.stationLevel == StationLevel.STATION_SET;
  }

  isStationAssignmentStrategyDisabled(position: RoutingRulePosition): boolean {
    return this.isLoading || position.stationLevel !== StationLevel.STATION_SET;
  }

  get cancelDisabled() {
    if (this.form == null) {
      return true;
    }

    if (!this.form.dirty) {
      return true;
    }

    if (this.isLoading) {
      return true;
    }

    return false;
  }

  get saveDisabled() {

    if (!this.form.dirty) {
      return true;
    }

    if (this.isLoading) {
      return true;
    }

    return this.form.invalid || !this.checkPositions();
  }

  duplicateNameExists() {
    if (this.rules && this.selectedRule) {
      return this.rules.some(rule => rule.id !== this.selectedRule.id && rule.name === this.workingCopyRule.name);
    }
    return false;
  }

  checkPositions(): boolean {
    let index = 0;
    for (const position of this.workingCopyRule.positions) {

      // First station must be valid
      if (index === 0) {
        if (!position.stationType) {
          return false;
        }
        if (position.stationType !== StationType.WAITING_AREA && (!position.stationId || !position.actionType)) {
          return false;
        }
      } else if (position.stationType && position.stationType !== StationType.WAITING_AREA &&
        (!position.stationId || !position.actionType)) {
        // Non Waiting areas must be filled completely
        return false;
      }
      index++;
    }
    return true;
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.workingCopyRule.positions, event.previousIndex, event.currentIndex);
    for (let i = 0; i < this.workingCopyRule.positions.length; i++) {
      this.workingCopyRule.positions[i].index = i;
    }
    this.form.form.markAsDirty();
  }

  removePosition(position: RoutingRulePosition) {
    const index = this.workingCopyRule.positions.indexOf(position);

    this.workingCopyRule.positions.splice(index, 1);

    if (position.id > 0) {
      position.deleted = true;
      this.workingCopyRule.deletedPositions.push(position);
    }

    if (this.workingCopyRule.positions.length === 0) {
      this.addPosition();
    }

    this.form.form.markAsDirty();
  }

  clear() {
    this.workingCopyRule = this.createNewRule();
    this.selectedRule = JSON.parse(JSON.stringify(this.workingCopyRule));
  }

  addRoutingRule() {
    if (this.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.routing-rule.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.routing-rule.discard.confirm-dialog.message',
        confirm: () => {
          this.workingCopyRule = this.createNewRule();
          this.selectedRule = JSON.parse(JSON.stringify(this.workingCopyRule));
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.workingCopyRule = this.createNewRule();
      this.selectedRule = JSON.parse(JSON.stringify(this.workingCopyRule));
    }
  }

  assignStation(position: RoutingRulePosition, form: NgForm) {

    const structureSelection = new StructureSelection();

    // TODO structureSelectionService erweitern
    // selectStationEvent.includeStations = true;
    // selectStationEvent.includeWaitingareas = false;
    if (position.stationLevel === StationLevel.STATION_GROUP) {
      structureSelection.stationGroupId = position.stationId;
    } else if (position.stationLevel === StationLevel.STATION_SET) {
      structureSelection.stationSetId = position.stationId;
    } else if (position.stationLevel === StationLevel.STATION) {
      structureSelection.stationId = position.stationId;
    }

    this.structureSelectionService.selectStationSetOrStation(structureSelection).subscribe(
      value => {

        if (value != null) {
          const selection: StructureSelection = value;

          if (selection.stationId != null) {
            position.stationLevel = StationLevel.STATION;
            position.stationId = selection.stationId;
            position.stationType = StationType.WORKING_AREA;
            const station = this.tmcContextService.findStationById(selection.stationId);
            position.stationName = station.name;
            if (station.stationType === StationType.WAITING_AREA) {
              position.stationType = StationType.WAITING_AREA;
              position.actionType = ActionType.NONE;
              position.waitingForRetrieve = false;
            }
          } else if (selection.stationSetId != null) {
            position.stationLevel = StationLevel.STATION_SET;
            position.stationId = selection.stationSetId;
            const stationSet = this.tmcContextService.findStationSetById(selection.stationSetId);
            position.stationName = stationSet.name;
            position.stationType = StationType.WORKING_AREA;
          } else if (selection.stationGroupId != null) {
            position.stationLevel = StationLevel.STATION_GROUP;
            position.stationId = selection.stationGroupId;
            position.stationName = this.tmcContextService.findStationGroupById(selection.stationGroupId).name;
            position.stationType = StationType.WORKING_AREA;
          }

          this.markPositionFormControlAsDirty(position, form);
        }
      }
    );
  }

  private markPositionFormControlAsDirty(position: RoutingRulePosition, form: NgForm) {
    this.markFormControlAsDirty(this.getPositionActionTypeFieldName(this.workingCopyRule, position), form);
    this.markFormControlAsDirty(this.getPositionSelectStationFieldName(this.workingCopyRule, position), form);
    this.markFormControlAsDirty(this.getPositionWaitingForRetrieveCheckboxName(this.workingCopyRule, position), form);
  }

  /**
   * see https://github.com/angular/angular/issues/12281
   */
  private markFormControlAsDirty(fcName: string, form: NgForm) {
    if (form.form.controls[fcName]) {
      form.form.controls[fcName].markAsDirty();
      form.form.controls[fcName].markAsTouched();
      form.form.controls[fcName].updateValueAndValidity();
    }
  }

  addPosition() {
    const position = new RoutingRulePosition();

    let newPositionsCount = 0;
    if (this.workingCopyRule.positions.length > 0) {
      // get minimum id of positions of this rule
      const minId = this.workingCopyRule.positions.reduce((prev, curr) => {
        return prev < curr ? prev : curr;
      }).id;
      if (minId != null && minId < 0) {
        // set newPositionsCount to absolute value of minimum id
        newPositionsCount = Math.abs(minId);
      }
    }
    newPositionsCount++;

    position.id = -(newPositionsCount);
    position.index = this.workingCopyRule.positions.length;
    position.actionType = ActionType.UNLOAD;
    this.workingCopyRule.positions.push(position);

    this.form.form.markAsDirty();
  }

  createNewRule(): RoutingRule {
    const rule = new RoutingRule({ positions: new Array<RoutingRulePosition>() });
    rule.id = -1;
    const position = new RoutingRulePosition();
    position.id = -1;
    position.index = 0;
    rule.positions.push(position);
    return rule;
  }

  showErrorMinlength(ctrl: NgModel) {
    return (ctrl.touched || ctrl.dirty) && ctrl.hasError('minlength');
  }

  showErrorMaxlength(ctrl: NgModel) {
    return (ctrl.touched || ctrl.dirty) && ctrl.hasError('maxlength');
  }

  showErrorRequired(ctrl: NgModel) {
    return (ctrl.touched || ctrl.dirty) && ctrl.hasError('required');
  }

  getPositionSelectStationFieldName(rule: RoutingRule, position: RoutingRulePosition) {
    return 'pos_select_station_' + rule.id.toString() + '_' + position.id.toString();
  }

  getPositionWaitingForRetrieveCheckboxName(rule: RoutingRule, position: RoutingRulePosition) {
    return 'pos_waiting_for_retrieve_' + rule.id.toString() + '_' + position.id.toString();
  }

  getPositionCargoMandatoryCheckboxName(rule: RoutingRule, position: RoutingRulePosition) {
    return 'pos_cargo_mandatory_' + rule.id.toString() + '_' + position.id.toString();
  }

  getPositionActionTypeFieldName(rule: RoutingRule, position: RoutingRulePosition) {
    return 'pos_action_type_' + rule.id.toString() + '_' + position.id.toString();
  }

  getPredictedDurationFieldName(rule: RoutingRule, position: RoutingRulePosition) {
    return 'pos_predicted_duration' + rule.id.toString() + '_' + position.id.toString();
  }

  getHintFieldName(rule: RoutingRule, position: RoutingRulePosition) {
    return 'pos_hint' + rule.id.toString() + '_' + position.id.toString();
  }

  getAssignToProcessingStationFirstFieldName(rule: RoutingRule, position: RoutingRulePosition) {
    return 'pos_assign_to_processing_station_first' + rule.id.toString() + '_' + position.id.toString();
  }

  assignTasks(position: RoutingRulePosition, form: NgForm) {
    const editTasks: Task[] = [];
    if (position.tasks) {
      for (const t of position.tasks) {
        const dTask = new Task({});
        dTask.id = t.id;
        dTask.relevance = t.relevance;
        dTask.processingStatus = t.processingStatus;
        editTasks.push(dTask);
      }
    }

    this.taskAssignmentService.openAssignmentDialog(editTasks, position.processingStatusModel).subscribe(next => {
      if (next != null) {
        position.tasks = [];
        for (const n of next) {
          position.tasks.push({ id: n.id, relevance: n.relevance, processingStatus: n.processingStatus });
        }
        this.form.form.markAsDirty();
      }
    });
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelectedRule(entry as RoutingRule);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addRoutingRule();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.deleteRoutingRule(entry as RoutingRule);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
